import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
    Booking,
    BookingsRequestParams,
    CancelBookingRequestParams,
    ClosestBookingRequestParams,
    CreateBookingRequestParams,
    Device,
    DeviceFullInfo,
    DevicesLocation,
    DevicesResponse,
    DevicesSearchRequestParams,
    UpdateBookingRequestParams,
} from '@pages/booking/types';
import { AxiosError, AxiosResponse } from 'axios';
import isEqual from 'lodash/isEqual';
import { RequestStatusEnum } from '@shared/types';

export type BookingState = {
    getDevicesStatus: RequestStatusEnum;
    getDevicesError: PayloadAction<AxiosError> | null;
    devices: DevicesResponse | null;

    getLocationsStatus: RequestStatusEnum;
    getLocationsError: PayloadAction<AxiosError> | null;
    locations: DevicesLocation[];

    getFullDeviceInfoStatus: RequestStatusEnum;
    getFullDeviceInfoError: PayloadAction<AxiosError> | null;
    fullDeviceInfo: DeviceFullInfo | null;

    getBookingsStatus: RequestStatusEnum;
    getBookingsError: PayloadAction<AxiosError> | null;
    bookings: Booking[];
    bookingsLocal: Record<Device['pk'], Booking[]>;
    bookingsSelected: Record<Device['pk'], Booking>;
    lastGetBookingsParams: BookingsRequestParams | null;

    getClosestBookingStatus: RequestStatusEnum;
    getClosestBookingError: PayloadAction<AxiosError> | null;
    closestBooking: Booking | null;

    createBookingStatus: RequestStatusEnum;
    createBookingError: PayloadAction<AxiosError> | null;

    updateBookingStatus: RequestStatusEnum;
    updateBookingError: PayloadAction<AxiosError> | null;

    cancelBookingStatus: RequestStatusEnum;
    cancelBookingError: PayloadAction<AxiosError> | null;
};

const initialState: BookingState = {
    getDevicesStatus: RequestStatusEnum.NotRequested,
    getDevicesError: null,
    devices: null,

    getLocationsStatus: RequestStatusEnum.NotRequested,
    getLocationsError: null,
    locations: [],

    getFullDeviceInfoStatus: RequestStatusEnum.NotRequested,
    getFullDeviceInfoError: null,
    fullDeviceInfo: null,

    getBookingsStatus: RequestStatusEnum.NotRequested,
    getBookingsError: null,
    bookings: [],
    bookingsLocal: {},
    bookingsSelected: {},
    lastGetBookingsParams: null,

    getClosestBookingStatus: RequestStatusEnum.NotRequested,
    getClosestBookingError: null,
    closestBooking: null,

    createBookingStatus: RequestStatusEnum.NotRequested,
    createBookingError: null,

    updateBookingStatus: RequestStatusEnum.NotRequested,
    updateBookingError: null,

    cancelBookingStatus: RequestStatusEnum.NotRequested,
    cancelBookingError: null,
};

const bookingSlice = createSlice({
    name: '@booking',
    initialState,
    reducers: {
        getBookingDevicesRequest(state, _action: PayloadAction<DevicesSearchRequestParams>) {
            state.getDevicesStatus = RequestStatusEnum.Loading;
            state.getDevicesError = null;
            state.devices = null;
        },
        getBookingDevicesSuccess(state, action: PayloadAction<AxiosResponse<BookingState['devices']>>) {
            state.getDevicesStatus = RequestStatusEnum.Success;
            state.getDevicesError = null;
            state.devices = action.payload.data || { dut: [], ixia: [], spirent: [] };
        },
        getBookingDevicesError(state, action: PayloadAction<AxiosError>) {
            state.getDevicesStatus = RequestStatusEnum.Error;
            state.getDevicesError = action;
            state.devices = null;
        },
        getBookingDevicesReset(state) {
            state.getDevicesStatus = RequestStatusEnum.NotRequested;
            state.getDevicesError = null;
            state.devices = null;
        },

        getBookingDevicesLocationsRequest(state) {
            state.getLocationsStatus = RequestStatusEnum.Loading;
            state.getLocationsError = null;
            state.locations = [];
        },
        getBookingDevicesLocationsSuccess(state, action: PayloadAction<AxiosResponse<BookingState['locations']>>) {
            state.getLocationsStatus = RequestStatusEnum.Success;
            state.getLocationsError = null;
            state.locations = action.payload.data || [];
        },
        getBookingDevicesLocationsError(state, action: PayloadAction<AxiosError>) {
            state.getLocationsStatus = RequestStatusEnum.Error;
            state.getLocationsError = action;
            state.locations = [];
        },
        getBookingDevicesLocationsReset(state) {
            state.getLocationsStatus = RequestStatusEnum.NotRequested;
            state.getLocationsError = null;
            state.locations = [];
        },

        getFullDeviceInfoRequest(state, _action: PayloadAction<Device['pk']>) {
            state.getFullDeviceInfoStatus = RequestStatusEnum.Loading;
            state.getFullDeviceInfoError = null;
            state.fullDeviceInfo = null;
        },
        getFullDeviceInfoSuccess(state, action: PayloadAction<AxiosResponse<BookingState['fullDeviceInfo']>>) {
            state.getFullDeviceInfoStatus = RequestStatusEnum.Success;
            state.getFullDeviceInfoError = null;
            state.fullDeviceInfo = action.payload.data;
        },
        getFullDeviceInfoError(state, action: PayloadAction<AxiosError>) {
            state.getFullDeviceInfoStatus = RequestStatusEnum.Error;
            state.getFullDeviceInfoError = action;
            state.fullDeviceInfo = null;
        },
        getFullDeviceInfoReset(state) {
            state.getFullDeviceInfoStatus = RequestStatusEnum.NotRequested;
            state.getFullDeviceInfoError = null;
            state.fullDeviceInfo = null;
        },

        getBookingsRequest(state, action: PayloadAction<BookingsRequestParams>) {
            if (action.payload.requestMode === 'standard') {
                state.getBookingsStatus = RequestStatusEnum.Loading;
                state.getBookingsError = null;
                state.bookings = [];
                state.lastGetBookingsParams = action.payload;
            }
        },
        getBookingsSuccess(state, action: PayloadAction<AxiosResponse<BookingState['bookings']>>) {
            state.getBookingsStatus = RequestStatusEnum.Success;
            state.getBookingsError = null;
            const newBookingsValue = action.payload.data || [];

            // update state only if it changed
            if (!isEqual(state.bookings, newBookingsValue)) {
                state.bookings = newBookingsValue;
            }
        },
        getBookingsError(state, action: PayloadAction<AxiosError>) {
            state.getBookingsStatus = RequestStatusEnum.Error;
            state.getBookingsError = action;
        },
        getBookingsReset(state) {
            state.getBookingsStatus = RequestStatusEnum.NotRequested;
            state.getBookingsError = null;
            state.bookings = [];
        },
        clearBookingsState(state) {
            state.bookings = [];
        },
        createLocalBooking(state, action: PayloadAction<{ deviceId: Device['pk']; booking: Booking }>) {
            const { deviceId, booking } = action.payload;
            state.bookingsLocal[deviceId] = [...(state.bookingsLocal[deviceId] || []), { ...booking, isLocal: true }];
        },
        updateLocalBooking(state, action: PayloadAction<{ deviceId: Device['pk']; booking: Booking }>) {
            const { deviceId, booking } = action.payload;

            if (!state.bookingsLocal[deviceId]?.length) {
                return;
            }

            const bookingObjectIndex = state.bookingsLocal[deviceId].findIndex((b) => b.pk === booking.pk);

            if (bookingObjectIndex !== -1) {
                state.bookingsLocal[deviceId][bookingObjectIndex] = booking;
            }
        },
        deleteLocalBooking(state, action: PayloadAction<{ deviceId: Device['pk']; bookingId: Booking['pk'] }>) {
            const { deviceId, bookingId } = action.payload;

            if (!state.bookingsLocal[deviceId]?.length) {
                return;
            }

            state.bookingsLocal[deviceId] = state.bookingsLocal[deviceId].filter((b) => b.pk !== bookingId);

            if (Array.isArray(state.bookingsLocal[deviceId]) && !state.bookingsLocal[deviceId].length) {
                delete state.bookingsLocal[deviceId];
            }
        },
        clearLocalBookings(state) {
            state.bookingsLocal = {};
            state.bookingsSelected = {};
        },
        clearSelectedBookings(state) {
            state.bookingsSelected = {};
        },
        selectBooking(state, action: PayloadAction<{ deviceId: Device['pk']; booking: Booking }>) {
            const { deviceId, booking } = action.payload;
            state.bookingsSelected[deviceId] = booking;
        },
        unselectBooking(state, action: PayloadAction<{ deviceId: Device['pk'] }>) {
            const { deviceId } = action.payload;
            delete state.bookingsSelected[deviceId];
        },

        getClosestBookingRequest(state, _action: PayloadAction<ClosestBookingRequestParams>) {
            state.getClosestBookingStatus = RequestStatusEnum.Loading;
            state.getClosestBookingError = null;
            state.closestBooking = null;
        },
        getClosestBookingSuccess(state, action: PayloadAction<AxiosResponse<Booking | ''>>) {
            state.getClosestBookingStatus = RequestStatusEnum.Success;
            state.getClosestBookingError = null;
            state.closestBooking = action.payload.data || null;
        },
        getClosestBookingError(state, action: PayloadAction<AxiosError>) {
            state.getClosestBookingStatus = RequestStatusEnum.Error;
            state.getClosestBookingError = action;
            state.closestBooking = null;
        },
        getClosestBookingReset(state) {
            state.getClosestBookingStatus = RequestStatusEnum.NotRequested;
            state.getClosestBookingError = null;
            state.closestBooking = null;
        },

        createBookingRequest(state, _action: PayloadAction<CreateBookingRequestParams>) {
            state.createBookingStatus = RequestStatusEnum.Loading;
            state.createBookingError = null;
        },
        createBookingSuccess(state, action: PayloadAction<AxiosResponse<Booking>>) {
            state.createBookingStatus = RequestStatusEnum.Success;
            state.createBookingError = null;
            state.bookings.push(action.payload.data);
        },
        createBookingError(state, action: PayloadAction<AxiosError>) {
            state.createBookingStatus = RequestStatusEnum.Error;
            state.createBookingError = action;
        },
        createBookingReset(state) {
            state.createBookingStatus = RequestStatusEnum.NotRequested;
            state.createBookingError = null;
        },

        updateBookingRequest(state, _action: PayloadAction<UpdateBookingRequestParams>) {
            state.updateBookingStatus = RequestStatusEnum.Loading;
            state.updateBookingError = null;
        },
        updateBookingSuccess(state, action: PayloadAction<AxiosResponse<Booking>>) {
            state.updateBookingStatus = RequestStatusEnum.Success;
            state.updateBookingError = null;

            const updatedBooking = action.payload.data;

            state.bookings = state.bookings.map((booking) => {
                if (booking.pk === updatedBooking.pk) {
                    return updatedBooking;
                }

                return booking;
            });
        },
        updateBookingError(state, action: PayloadAction<AxiosError>) {
            state.updateBookingStatus = RequestStatusEnum.Error;
            state.updateBookingError = action;
        },
        updateBookingReset(state) {
            state.updateBookingStatus = RequestStatusEnum.NotRequested;
            state.updateBookingError = null;
        },

        cancelBookingRequest(state, _action: PayloadAction<CancelBookingRequestParams>) {
            state.cancelBookingStatus = RequestStatusEnum.Loading;
            state.cancelBookingError = null;
        },
        cancelBookingSuccess(state, action: PayloadAction<CancelBookingRequestParams & { newBookingObject: Booking }>) {
            state.cancelBookingStatus = RequestStatusEnum.Success;
            state.cancelBookingError = null;
            const { bookingId, isInProgress, newBookingObject } = action.payload;

            if (isInProgress) {
                state.bookings = state.bookings.map((booking) => {
                    if (booking.pk === bookingId) {
                        return newBookingObject;
                    }

                    return booking;
                });
            } else {
                state.bookings = state.bookings.filter((booking) => booking.pk !== bookingId);
            }
        },
        cancelBookingError(state, action: PayloadAction<AxiosError>) {
            state.cancelBookingStatus = RequestStatusEnum.Error;
            state.cancelBookingError = action;
        },
        cancelBookingReset(state) {
            state.cancelBookingStatus = RequestStatusEnum.NotRequested;
            state.cancelBookingError = null;
        },
    },
});

export default bookingSlice.reducer;

export const {
    getBookingDevicesRequest,
    getBookingDevicesSuccess,
    getBookingDevicesError,
    getBookingDevicesReset,
    getBookingDevicesLocationsRequest,
    getBookingDevicesLocationsSuccess,
    getBookingDevicesLocationsError,
    getBookingDevicesLocationsReset,
    getFullDeviceInfoRequest,
    getFullDeviceInfoSuccess,
    getFullDeviceInfoError,
    getFullDeviceInfoReset,
    getBookingsRequest,
    getBookingsSuccess,
    getBookingsError,
    getBookingsReset,
    clearBookingsState,
    createLocalBooking,
    updateLocalBooking,
    deleteLocalBooking,
    clearLocalBookings,
    clearSelectedBookings,
    selectBooking,
    unselectBooking,
    getClosestBookingRequest,
    getClosestBookingSuccess,
    getClosestBookingError,
    getClosestBookingReset,
    createBookingRequest,
    createBookingSuccess,
    createBookingError,
    createBookingReset,
    updateBookingRequest,
    updateBookingSuccess,
    updateBookingError,
    updateBookingReset,
    cancelBookingRequest,
    cancelBookingSuccess,
    cancelBookingError,
    cancelBookingReset,
} = bookingSlice.actions;
